///
/// EarthBrowser
///
/// Created by Matt Giger on 1/7/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { type TileSeries } from "@/earthbrowser/datasets/TileSeries"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"
import { getTileToken } from "@/services/tiles/TileTokenService"
import { animation, easing } from "@stovokor/math/animation"
import { vec2 } from "@stovokor/math/vector"
import { type matrix } from "@stovokor/math/matrix"
import { type Material } from "@stovokor/shader/Material"
import { Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type WebMercatorTileId } from "./WebMercatorTileId"

const cFadeSpeed = 0.5

// Transient tile load failures (network, timeout, 5xx, token minting) are
// retried with exponential backoff instead of leaving the tile stuck on a
// stale or blank texture until the next slot. Definitive answers (empty
// 204 tile, 403 outside the entitlement window, 404 missing archive) are
// not retried
const cRetryBaseMs = 2000
const cRetryMaxMs = 30_000
const cMaxRetries = 5

export class WebMercatorOverlayTileMaterial implements Material {
	private context: EarthBrowserContext
	private tileId: WebMercatorTileId
	private tileSeries: TileSeries

	public available: boolean = false
	public transparency: boolean = true

	public mercatorOrigin: vec2
	public mercatorDelta: vec2

	// Per-frame uniforms must be UniformDynamic: plain fields are captured
	// once at shader setup, so later mutations would never reach the GPU

	// Current time frame texture
	public tileTexture = new UniformDynamic<Texture>()
	// Next time frame texture (for time blending)
	public nextTileTexture = new UniformDynamic<Texture>()
	// Blend factor between current (0) and next (1) time frame
	public timeBlend = new UniformDynamic<number>()
	// Frame-to-frame flow for the pair (equirect UV displacement, from the
	// series; the blank texture is zero flow = in-place dissolve)
	public tileFlow = new UniformDynamic<Texture>()
	// Series-wide opacity (TileSeries.fade): the observed radar yielding to
	// the forecast overlay
	public seriesFade = new UniformDynamic<number>()
	// Footprints of the drawing 3D volumes the tiles step back under
	// (radar_common.glsl, fed from context.radarVolumeMask)
	public radarVolumeMask = new UniformDynamic<matrix>()
	public radarVolumeMaskCount = new UniformDynamic<number>()

	// Parent tile texture (for spatial LOD blending)
	public parentTileTexture = new UniformDynamic<Texture>()
	public parentDelta = new vec2(0, 0)
	public textureFade = new UniformDynamic<number>()

	// Texture objects backing the uniforms (source of truth; the
	// UniformDynamic values mirror these)
	private currentTex: Texture
	private nextTex: Texture

	// Texture loading state for current frame
	private currentLoader?: ImageLoader
	private currentLoading: boolean = false
	private currentLoaded: boolean = false
	private currentUrlTemplate: string = ""
	private currentAttempts: number = 0
	// performance.now() at which a failed load may be retried (0 = none pending)
	private currentRetryAt: number = 0

	// Texture loading state for next frame
	private nextLoader?: ImageLoader
	private nextLoading: boolean = false
	private nextLoaded: boolean = false
	private nextUrlTemplate: string = ""
	private nextAttempts: number = 0
	private nextRetryAt: number = 0

	public textureLoaded: boolean = false

	/** A current- or next-frame texture fetch is in flight (render-mode settle gate) */
	public get loadPending(): boolean {
		return this.currentLoading || this.nextLoading
	}

	// Parent material whose current texture we sample for the LOD fade; the
	// backing Texture object changes when the parent promotes its next frame,
	// so the uniform is refreshed from this each frame
	private parentMaterial?: WebMercatorOverlayTileMaterial

	private fadeAnimation: animation
	private fadeStarted: boolean = false

	constructor(
		context: EarthBrowserContext,
		tileId: WebMercatorTileId,
		tileSeries: TileSeries,
		parentMaterial?: WebMercatorOverlayTileMaterial
	) {
		this.context = context
		this.tileId = tileId
		this.tileSeries = tileSeries

		const n = 1 << tileId.z
		this.mercatorOrigin = new vec2(tileId.x / n, tileId.y / n)
		this.mercatorDelta = new vec2(1 / n, 1 / n)

		this.currentTex = context.blankTexture
		this.nextTex = context.blankTexture
		this.tileTexture.value = this.currentTex
		this.nextTileTexture.value = this.nextTex
		this.parentTileTexture.value = context.blankTexture
		this.timeBlend.value = 0
		this.tileFlow.value = context.blankTexture
		this.seriesFade.value = 1
		this.textureFade.value = 1

		this.fadeAnimation = new animation()
			.from(1).to(0)
			.duration(cFadeSpeed)
			.easing(easing.cubicInOut)
			.onUpdate((value: number) => {
				this.textureFade.value = value
			})
			.onComplete(() => {
				this.textureFade.value = 0
			})

		if (parentMaterial) {
			this.parentMaterial = parentMaterial
			this.parentTileTexture.value = parentMaterial.currentTexture
			const childIdx = ((tileId.y % 2) << 1) | (tileId.x % 2)
			const dx = 0.5 * (childIdx % 2)
			const dy = 0.5 * Math.floor(childIdx / 2)
			this.parentDelta = new vec2(dx, dy)
		} else {
			this.textureFade.value = 0
			this.fadeStarted = true
		}

		// Initial load based on current series state
		this.updateFromSeries()
	}

	public destroy(): void {
		this.available = false
		this.currentLoader?.abort()
		this.nextLoader?.abort()

		if (this.currentTex !== this.context.blankTexture) {
			this.currentTex.destroy()
		}
		if (this.nextTex !== this.context.blankTexture) {
			this.nextTex.destroy()
		}
	}

	public get readyToBeAParent(): boolean {
		return this.currentLoaded
	}

	/** Texture children sample for their parent-fade blend */
	public get currentTexture(): Texture {
		return this.currentTex
	}

	private tryStartFadeAnimation(): void {
		if (!this.fadeStarted && this.currentLoaded) {
			this.fadeStarted = true
			this.fadeAnimation.start()
		}
	}

	private getTileUrl(urlTemplate: string): string {
		return urlTemplate
			.replace(/\{z\}/g, String(this.tileId.z))
			.replace(/\{x\}/g, String(this.tileId.x))
			.replace(/\{y\}/g, String(this.tileId.y))
	}

	/**
	 * Update texture loading based on current TileSeries state.
	 * Called each frame to check if we need to load new textures.
	 */
	public updateFromSeries(): void {
		const currentTemplate = this.tileSeries.currentUrlTemplate
		const nextTemplate = this.tileSeries.nextUrlTemplate
		const now = performance.now()

		// Keep the parent-fade texture pointing at the parent's live current
		// texture: the backing object changes when the parent swaps in a
		// promoted next frame
		if (this.parentMaterial) {
			this.parentTileTexture.value = this.parentMaterial.currentTexture
		}

		// Check if current frame changed
		if (currentTemplate && currentTemplate !== this.currentUrlTemplate) {
			if (currentTemplate === this.nextUrlTemplate && this.nextLoaded) {
				// The clock advanced into the frame we were blending toward:
				// its image is already decoded in nextTex, so swap the
				// textures instead of re-downloading. Reloading into the
				// live texture left the OLD frame visible (with the blend
				// reset to 0) until the fetch+decode finished, snapping the
				// radar animation backward one frame on every slot advance
				this.promoteNextToCurrent()
			} else {
				this.loadCurrentTexture(currentTemplate)
			}
		} else if (currentTemplate && this.currentRetryAt > 0 && !this.currentLoading && now >= this.currentRetryAt) {
			this.loadCurrentTexture(currentTemplate)
		} else if (!currentTemplate && this.currentUrlTemplate) {
			// The clock moved before the first frame: the series shows no
			// data, so stop showing the stale slot (the mosaic texture
			// underneath is blank there too)
			this.clearCurrentTexture()
		}

		// Check if next frame changed
		if (nextTemplate && nextTemplate !== this.nextUrlTemplate) {
			this.loadNextTexture(nextTemplate)
		} else if (nextTemplate && this.nextRetryAt > 0 && !this.nextLoading && now >= this.nextRetryAt) {
			this.loadNextTexture(nextTemplate)
		} else if (!nextTemplate && this.nextUrlTemplate) {
			// No next frame anymore
			this.nextLoader?.abort()
			this.nextLoader = undefined
			this.nextLoading = false
			this.nextUrlTemplate = ""
			this.nextLoaded = false
			this.nextRetryAt = 0
			this.nextAttempts = 0
			if (this.nextTex !== this.context.blankTexture) {
				this.nextTex.destroy()
				this.nextTex = this.context.blankTexture
				this.nextTileTexture.value = this.nextTex
			}
		}

		// Update time blend factor (after the frame bookkeeping above so the
		// loaded flags are current). Only blend once the next frame has
		// actually loaded - otherwise blending fades echoes toward a blank
		// texture instead of the next timestamp
		this.timeBlend.value = this.nextLoaded ? this.tileSeries.blendFactor : 0
		// The flow field the mosaic estimated for the same slot pair; blank
		// (zero flow) until it is ready, which degrades to the plain dissolve
		this.tileFlow.value = this.tileSeries.flowTexture() ?? this.context.blankTexture
		this.seriesFade.value = this.tileSeries.fade()
		this.radarVolumeMask.value = this.context.radarVolumeMask.mask
		this.radarVolumeMaskCount.value = this.context.radarVolumeMask.count

		// Update availability
		this.textureLoaded = this.currentLoaded
		this.available = this.currentLoaded
	}

	/**
	 * The series advanced into the frame whose image is already loaded in
	 * nextTex: swap the texture objects so it becomes the current texture,
	 * and retire the old current texture for reuse by the upcoming
	 * next-frame load
	 */
	private promoteNextToCurrent(): void {
		// A stale reload of the previous current frame may still be in
		// flight - it must not upload into the texture we're about to reuse
		this.currentLoader?.abort()
		this.currentLoader = undefined
		this.currentLoading = false
		this.currentRetryAt = 0
		this.currentAttempts = 0

		const previousTex = this.currentTex
		this.currentTex = this.nextTex
		this.nextTex = previousTex
		this.tileTexture.value = this.currentTex
		this.nextTileTexture.value = this.nextTex

		this.currentUrlTemplate = this.nextUrlTemplate
		this.currentLoaded = true

		// nextTex now holds the stale previous frame; clearing the template
		// and loaded flag makes updateFromSeries start a fresh next-frame
		// load, and keeps timeBlend at 0 until it completes
		this.nextUrlTemplate = ""
		this.nextLoaded = false
		this.nextRetryAt = 0
		this.nextAttempts = 0

		this.tryStartFadeAnimation()
	}

	private clearCurrentTexture(): void {
		this.currentLoader?.abort()
		this.currentLoader = undefined
		this.currentLoading = false
		this.currentLoaded = false
		this.currentUrlTemplate = ""
		this.currentRetryAt = 0
		this.currentAttempts = 0
	}

	/**
	 * Backoff before retrying a failed load, or undefined when the failure
	 * is definitive for this slot (nothing to retry) or retries are spent
	 */
	private retryDelayMs(loader: ImageLoader, attempts: number): number | undefined {
		if (loader.aborted || loader.noContent) {
			return undefined
		}
		const status = loader.status
		if (status === 403 || status === 404) {
			return undefined
		}
		if (attempts >= cMaxRetries) {
			return undefined
		}
		return Math.min(cRetryMaxMs, cRetryBaseMs * 2 ** attempts)
	}

	private loadCurrentTexture(urlTemplate: string): void {
		// Skip if already loading this template
		if (this.currentLoading && this.currentUrlTemplate === urlTemplate) {
			return
		}
		if (urlTemplate !== this.currentUrlTemplate) {
			this.currentAttempts = 0
		}
		this.currentRetryAt = 0

		// Abort existing load
		this.currentLoader?.abort()
		this.currentUrlTemplate = urlTemplate
		this.currentLoading = true

		const url = this.getTileUrl(urlTemplate)

		// Reuse existing texture if possible, otherwise create new
		if (this.currentTex === this.context.blankTexture) {
			this.currentTex = new Texture(this.context)
			this.tileTexture.value = this.currentTex
		}

		const loader = new ImageLoader(url, {
			getAuthToken: getTileToken
		})
		this.currentLoader = loader
		// R8 grayscale: the shader only reads .r (decodeReflectivity), so
		// RGBA + mips would spend ~5x the GPU memory for no visual gain
		loader.loadGrayscaleIntoTexture(this.currentTex)
			.then((success) => {
				// Superseded (aborted, promoted or cleared) - its outcome
				// must not clobber the state of the load that replaced it
				if (this.currentLoader !== loader) {
					return
				}
				this.currentLoaded = success
				this.currentLoading = false
				this.currentLoader = undefined
				this.available = success
				this.textureLoaded = success
				if (success) {
					this.currentAttempts = 0
					this.tryStartFadeAnimation()
				} else {
					this.currentAttempts++
					const delay = this.retryDelayMs(loader, this.currentAttempts)
					this.currentRetryAt = delay === undefined ? 0 : performance.now() + delay
				}
			})
			.catch(() => {
				if (this.currentLoader !== loader) {
					return
				}
				this.currentLoading = false
				this.currentLoader = undefined
				this.currentAttempts++
				const delay = this.retryDelayMs(loader, this.currentAttempts)
				this.currentRetryAt = delay === undefined ? 0 : performance.now() + delay
			})
	}

	private loadNextTexture(urlTemplate: string): void {
		// Skip if already loading this template
		if (this.nextLoading && this.nextUrlTemplate === urlTemplate) {
			return
		}
		if (urlTemplate !== this.nextUrlTemplate) {
			this.nextAttempts = 0
		}
		this.nextRetryAt = 0

		// Abort existing load
		this.nextLoader?.abort()
		this.nextUrlTemplate = urlTemplate
		this.nextLoading = true
		this.nextLoaded = false

		const url = this.getTileUrl(urlTemplate)

		// Reuse existing texture if possible, otherwise create new
		if (this.nextTex === this.context.blankTexture) {
			this.nextTex = new Texture(this.context)
			this.nextTileTexture.value = this.nextTex
		}

		const loader = new ImageLoader(url, {
			getAuthToken: getTileToken
		})
		this.nextLoader = loader
		// R8 grayscale, matching the current-frame texture (see above)
		loader.loadGrayscaleIntoTexture(this.nextTex)
			.then((success) => {
				if (this.nextLoader !== loader) {
					return
				}
				this.nextLoaded = success
				this.nextLoading = false
				this.nextLoader = undefined
				if (success) {
					this.nextAttempts = 0
				} else {
					this.nextAttempts++
					const delay = this.retryDelayMs(loader, this.nextAttempts)
					this.nextRetryAt = delay === undefined ? 0 : performance.now() + delay
				}
			})
			.catch(() => {
				if (this.nextLoader !== loader) {
					return
				}
				this.nextLoading = false
				this.nextLoader = undefined
				this.nextAttempts++
				const delay = this.retryDelayMs(loader, this.nextAttempts)
				this.nextRetryAt = delay === undefined ? 0 : performance.now() + delay
			})
	}
}
